import { Button } from "@/components/ui/button";
import { Helmet } from 'react-helmet-async';

const StyleGuide = () => {
  const colors = [
    { name: "Default Background", variable: "--brand-default", hex: "#FAFAF0", hsl: "60 33% 96%", usage: "Primary background for Hero, Impact, Slack sections" },
    { name: "Navy (Primary)", variable: "--brand-navy", hex: "#002178", hsl: "224 100% 24%", usage: "Main brand color, Testimonials section, Assessment CTA" },
    { name: "Alt Background", variable: "--brand-alt", hex: "#F5F5F0", hsl: "56 44% 93%", usage: "Problems/When to Call Us section" },
    { name: "Teal (Accent)", variable: "--brand-teal", hex: "#0047FF", hsl: "223 100% 50%", usage: "Accent color, use with /56 opacity for #0047FF8F" },
    { name: "Dark (Text)", variable: "--brand-dark", hex: "#000000D4", hsl: "0 0% 0% / 83%", usage: "H1, H2 headings & body text (black with 83% opacity)" },
  ];

  const sectionColors = [
    { section: "Hero", background: "Default (#FAFAF0)", textColor: "Dark text", rationale: "Clean entry point, focus on headline" },
    { section: "When to Call Us (Problems)", background: "Alt (#F5F5F0)", textColor: "Dark text", rationale: "Subtle lift from Hero, signals new topic" },
    { section: "Testimonials", background: "Navy (#002178)", textColor: "White text", rationale: "Authority spike — trust moment before proof" },
    { section: "Impact We've Driven", background: "Default (#FAFAF0)", textColor: "Dark text", rationale: "Clean backdrop for case study details" },
    { section: "Slack Screenshots", background: "Default (#FAFAF0)", textColor: "Dark text", rationale: "Neutral for visual carousel" },
    { section: "Assessment CTA", background: "Navy (#002178)", textColor: "White text", rationale: "Strong close — drives conversion" },
    { section: "Footer", background: "Default (#FAFAF0)", textColor: "Dark text", rationale: "Light, minimal distraction" },
  ];

  const typography = [
    { 
      name: "Hero Heading (H1)", 
      class: ".hero-heading", 
      font: "Manrope", 
      size: "4em", 
      weight: "600 (Semibold)", 
      lineHeight: "1.125",
      letterSpacing: "0",
      color: "#000000D4 (83% opacity)"
    },
    { 
      name: "Section Heading (H2)", 
      class: ".section-heading", 
      font: "Manrope", 
      size: "3em", 
      weight: "300 (Light)", 
      lineHeight: "1.125",
      letterSpacing: "0",
      color: "#000000D4 (83% opacity)"
    },
    { 
      name: "Subsection Heading (H3)", 
      class: ".subsection-heading", 
      font: "Manrope", 
      size: "2em", 
      weight: "300 (Light)", 
      lineHeight: "1.125",
      letterSpacing: "0",
      color: "Teal (#0047FF8F)"
    },
    { 
      name: "Body Text", 
      class: "Default", 
      font: "Inter", 
      size: "1rem / 1.125rem", 
      weight: "400 (Regular)", 
      lineHeight: "1.6",
      letterSpacing: "Normal",
      color: "#000000D4 (83% opacity)"
    },
  ];

  return (
    <div className="min-h-screen bg-brand-default">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      {/* Header */}
      <header className="bg-brand-navy text-white py-12 px-8">
        <div className="max-w-[1200px] mx-auto">
          <h1 className="text-4xl font-semibold font-sans mb-2">FractionalCPO Style Guide</h1>
          <p className="text-white/80">Design system documentation v2.0 — Conversion-optimized</p>
          <p className="text-white/60 text-sm mt-2">Previous version: <a href="/style-guide-v1" className="underline hover:text-white">v1.0</a></p>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-8 py-16 space-y-20">
        
        {/* Section Color Scheme - NEW */}
        <section>
          <h2 className="section-heading text-brand-dark/83 mb-4">Section Color Scheme</h2>
          <p className="text-brand-dark/70 mb-8">Optimized for B2B conversion: problem → trust → proof → action</p>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-brand-dark/20">
                  <th className="text-left py-4 pr-4 font-semibold text-brand-dark">Section</th>
                  <th className="text-left py-4 pr-4 font-semibold text-brand-dark">Background</th>
                  <th className="text-left py-4 pr-4 font-semibold text-brand-dark">Text</th>
                  <th className="text-left py-4 font-semibold text-brand-dark">Rationale</th>
                </tr>
              </thead>
              <tbody>
                {sectionColors.map((row, index) => (
                  <tr key={index} className="border-b border-brand-dark/10">
                    <td className="py-4 pr-4 font-medium text-brand-dark">{row.section}</td>
                    <td className="py-4 pr-4">
                      <span className={`inline-block px-3 py-1 rounded text-sm ${
                        row.background.includes('Navy') 
                          ? 'bg-brand-navy text-white' 
                          : row.background.includes('Alt') 
                            ? 'bg-brand-alt text-brand-dark' 
                            : 'bg-brand-default text-brand-dark border border-brand-dark/20'
                      }`}>
                        {row.background}
                      </span>
                    </td>
                    <td className="py-4 pr-4 text-brand-dark/70">{row.textColor}</td>
                    <td className="py-4 text-brand-dark/70 text-sm">{row.rationale}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Visual Flow */}
          <div className="mt-12">
            <h3 className="font-semibold text-brand-dark mb-4">Visual Flow Preview</h3>
            <div className="flex flex-col rounded-lg overflow-hidden border border-brand-dark/20">
              <div className="bg-brand-default p-4 text-brand-dark text-sm font-medium">Hero — Default</div>
              <div className="bg-brand-alt p-4 text-brand-dark text-sm font-medium">Problems — Alt</div>
              <div className="bg-brand-navy p-4 text-white text-sm font-medium">Testimonials — Navy (trust spike)</div>
              <div className="bg-brand-default p-4 text-brand-dark text-sm font-medium">Impact — Default</div>
              <div className="bg-brand-default p-4 text-brand-dark text-sm font-medium">Slack — Default</div>
              <div className="bg-brand-navy p-4 text-white text-sm font-medium">Assessment CTA — Navy (conversion)</div>
              <div className="bg-brand-default p-4 text-brand-dark text-sm font-medium">Footer — Default</div>
            </div>
          </div>
        </section>

        {/* Color Palette */}
        <section>
          <h2 className="section-heading text-brand-dark/83 mb-8">Color Palette</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {colors.map((color) => (
              <div key={color.name} className="border border-border rounded-lg overflow-hidden">
                <div 
                  className="h-24"
                  style={{ backgroundColor: color.hex }}
                />
                <div className="p-4 bg-white">
                  <h3 className="font-semibold text-brand-dark mb-2">{color.name}</h3>
                  <div className="space-y-1 text-sm text-brand-dark/70">
                    <p><span className="font-medium">HEX:</span> {color.hex}</p>
                    <p><span className="font-medium">HSL:</span> {color.hsl}</p>
                    <p><span className="font-medium">Variable:</span> <code className="bg-gray-100 px-1 rounded">{color.variable}</code></p>
                    <p className="pt-2 text-xs">{color.usage}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Color Usage Examples */}
          <div className="mt-8 p-6 bg-brand-alt rounded-lg">
            <h3 className="font-semibold text-brand-dark mb-4">Tailwind Usage</h3>
            <div className="space-y-2 font-mono text-sm">
              <p><code className="bg-white px-2 py-1 rounded">bg-brand-default</code> → Default background</p>
              <p><code className="bg-white px-2 py-1 rounded">bg-brand-navy</code> → Navy sections</p>
              <p><code className="bg-white px-2 py-1 rounded">bg-brand-alt</code> → Alternate backgrounds</p>
              <p><code className="bg-white px-2 py-1 rounded">text-brand-teal/56</code> → Accent text with opacity</p>
              <p><code className="bg-white px-2 py-1 rounded">text-brand-dark/83</code> → Body text with opacity</p>
            </div>
          </div>
        </section>

        {/* Typography */}
        <section>
          <h2 className="section-heading text-brand-dark/83 mb-8">Typography</h2>
          
          {/* Font Families */}
          <div className="mb-12 p-6 bg-brand-alt rounded-lg">
            <h3 className="font-semibold text-brand-dark mb-4">Font Families</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-2xl font-sans mb-2" style={{ fontFamily: 'Manrope' }}>Manrope</p>
                <p className="text-sm text-brand-dark/70">Used for all headings (H1, H2, H3)</p>
              </div>
              <div>
                <p className="text-2xl" style={{ fontFamily: 'Inter' }}>Inter</p>
                <p className="text-sm text-brand-dark/70">Used for body text and paragraphs</p>
              </div>
            </div>
          </div>

          {/* Type Scale */}
          <div className="space-y-8">
            {typography.map((type) => (
              <div key={type.name} className="border-b border-border pb-8">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="lg:w-1/2">
                    {type.class === ".hero-heading" && (
                      <h1 className="hero-heading text-brand-dark/83">Hero Heading</h1>
                    )}
                    {type.class === ".section-heading" && (
                      <h2 className="section-heading text-brand-dark/83">Section Heading</h2>
                    )}
                    {type.class === ".subsection-heading" && (
                      <h3 className="subsection-heading">Subsection Heading</h3>
                    )}
                    {type.class === "Default" && (
                      <p className="text-[1.125rem] text-brand-dark/83 leading-[1.6]">
                        This is body text. It uses the Inter font family at regular weight with comfortable line height for readability.
                      </p>
                    )}
                  </div>
                  <div className="lg:w-1/2 bg-brand-alt p-4 rounded-lg">
                    <h4 className="font-semibold text-brand-dark mb-3">{type.name}</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-brand-dark/70">
                      <p><span className="font-medium">Class:</span> <code className="bg-white px-1 rounded">{type.class}</code></p>
                      <p><span className="font-medium">Font:</span> {type.font}</p>
                      <p><span className="font-medium">Size:</span> {type.size}</p>
                      <p><span className="font-medium">Weight:</span> {type.weight}</p>
                      <p><span className="font-medium">Line Height:</span> {type.lineHeight}</p>
                      <p><span className="font-medium">Color:</span> {type.color}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Buttons */}
        <section>
          <h2 className="section-heading text-brand-dark/83 mb-8">Buttons</h2>
          
          <div className="space-y-8">
            {/* Primary Button */}
            <div className="p-6 bg-brand-alt rounded-lg">
              <h3 className="font-semibold text-brand-dark mb-4">Primary CTA Button (Light Background)</h3>
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                <Button className="bg-brand-navy hover:bg-brand-teal text-white font-bold tracking-wide text-sm px-10 h-12 rounded-full transition-all duration-250 hover:scale-105 font-sans">
                  Book a Strategy Call
                </Button>
                <div className="text-sm text-brand-dark/70 space-y-1">
                  <p><span className="font-medium">Height:</span> h-12 (48px)</p>
                  <p><span className="font-medium">Padding:</span> px-10</p>
                  <p><span className="font-medium">Font:</span> Manrope, Bold, tracking-wide</p>
                  <p><span className="font-medium">Shape:</span> rounded-full (pill)</p>
                  <p><span className="font-medium">Case:</span> Title Case (not uppercase)</p>
                  <p><span className="font-medium">Hover:</span> Scale 105%, bg-brand-teal</p>
                </div>
              </div>
            </div>

            {/* Outlined Button on Dark */}
            <div className="p-6 bg-brand-navy rounded-lg">
              <h3 className="font-semibold text-white mb-4">Outlined Button (Dark Background)</h3>
              <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-brand-navy font-bold tracking-wide text-sm px-10 h-12 rounded-full transition-all duration-250 hover:scale-105 font-sans">
                  Start Assessment
                </Button>
                <div className="text-sm text-white/70 space-y-1">
                  <p><span className="font-medium">Style:</span> Outlined with white border</p>
                  <p><span className="font-medium">Font:</span> Manrope, Bold, tracking-wide</p>
                  <p><span className="font-medium">Case:</span> Title Case</p>
                  <p><span className="font-medium">Hover:</span> Fill white, text navy</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Layout */}
        <section>
          <h2 className="section-heading text-brand-dark/83 mb-8">Layout Guidelines</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-brand-alt rounded-lg">
              <h3 className="font-semibold text-brand-dark mb-3">Container</h3>
              <ul className="text-sm text-brand-dark/70 space-y-2">
                <li>• Max width: <code className="bg-white px-1 rounded">max-w-[1200px]</code></li>
                <li>• Centered: <code className="bg-white px-1 rounded">mx-auto</code></li>
                <li>• Padding: <code className="bg-white px-1 rounded">px-8 md:px-16 lg:px-24</code></li>
              </ul>
            </div>
            
            <div className="p-6 bg-brand-alt rounded-lg">
              <h3 className="font-semibold text-brand-dark mb-3">Alignment</h3>
              <ul className="text-sm text-brand-dark/70 space-y-2">
                <li>• All sections: Left-aligned</li>
                <li>• Logo carousel: Full-width exception</li>
                <li>• Consistent gutters across breakpoints</li>
              </ul>
            </div>

            <div className="p-6 bg-brand-alt rounded-lg">
              <h3 className="font-semibold text-brand-dark mb-3">Section Backgrounds</h3>
              <ul className="text-sm text-brand-dark/70 space-y-2">
                <li>• Default: <code className="bg-white px-1 rounded">bg-brand-default</code> (#FAFAF0)</li>
                <li>• Navy: <code className="bg-white px-1 rounded">bg-brand-navy</code> (#002178)</li>
                <li>• Alternate: <code className="bg-white px-1 rounded">bg-brand-alt</code> (#F5F5F0)</li>
              </ul>
            </div>

            <div className="p-6 bg-brand-alt rounded-lg">
              <h3 className="font-semibold text-brand-dark mb-3">Spacing</h3>
              <ul className="text-sm text-brand-dark/70 space-y-2">
                <li>• Section padding: <code className="bg-white px-1 rounded">py-16 md:py-24</code></li>
                <li>• Between elements: Tailwind spacing scale</li>
                <li>• Consistent vertical rhythm</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Input Fields */}
        <section>
          <h2 className="section-heading text-brand-dark/83 mb-8">Form Elements</h2>
          
          <div className="p-6 bg-brand-alt rounded-lg">
            <h3 className="font-semibold text-brand-dark mb-4">Input Fields</h3>
            <div className="max-w-md space-y-4">
              <input 
                type="text" 
                placeholder="Name"
                className="w-full px-4 py-3 border border-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-navy"
              />
              <input 
                type="email" 
                placeholder="Email"
                className="w-full px-4 py-3 border border-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-navy"
              />
              <p className="text-sm text-brand-dark/70">
                • Thin border style with internal placeholders (no external labels)<br />
                • Focus state: ring with brand-navy color
              </p>
            </div>
          </div>
        </section>

      </div>

      {/* Footer */}
      <footer className="bg-brand-navy text-white py-8 px-8 text-center">
        <p className="text-white/70">FractionalCPO Design System v2.0 — Conversion-optimized</p>
      </footer>
    </div>
  );
};

export default StyleGuide;
